// Secure token storage utility with additional security measures

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  issuedAt: number;
}

class SecureTokenStorage {
  private readonly ACCESS_TOKEN_KEY = 'jarvis_access_token';
  private readonly REFRESH_TOKEN_KEY = 'jarvis_refresh_token';
  private readonly TOKEN_METADATA_KEY = 'jarvis_token_meta';
  private readonly MAX_INACTIVE_TIME = 2 * 60 * 60 * 1000; // 2 hours

  // Store tokens with metadata
  setTokens(accessToken: string, refreshToken: string): void {
    const now = Date.now();
    const metadata: TokenData = {
      accessToken,
      refreshToken,
      expiresAt: now + (2 * 60 * 60 * 1000), // 2 hours for access token to match backend
      issuedAt: now
    };

    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(this.TOKEN_METADATA_KEY, JSON.stringify(metadata));
      this.updateLastActivity();
    } catch (error) {
      console.error('Failed to store tokens:', error);
      // Fallback to sessionStorage if localStorage fails
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  // Get access token with validation
  getAccessToken(): string | null {
    if (!this.isSessionValid()) {
      this.clearTokens();
      return null;
    }

    this.updateLastActivity();
    return this.getFromStorage(this.ACCESS_TOKEN_KEY);
  }

  // Get refresh token with validation
  getRefreshToken(): string | null {
    if (!this.isSessionValid()) {
      this.clearTokens();
      return null;
    }

    return this.getFromStorage(this.REFRESH_TOKEN_KEY);
  }

  // Check if access token is about to expire
  isAccessTokenExpiring(): boolean {
    const metadata = this.getTokenMetadata();
    if (!metadata) return true;

    const timeUntilExpiry = metadata.expiresAt - Date.now();
    return timeUntilExpiry < (15 * 60 * 1000); // Less than 15 minutes
  }

  // Clear all tokens
  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_METADATA_KEY);
    localStorage.removeItem('jarvis_last_activity');
    
    // Also clear from sessionStorage
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  // Check if session is still valid based on inactivity
  isSessionValid(): boolean {
    const lastActivity = localStorage.getItem('jarvis_last_activity');
    if (!lastActivity) return false;

    const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
    return timeSinceLastActivity < this.MAX_INACTIVE_TIME;
  }

  // Update last activity timestamp
  updateLastActivity(): void {
    localStorage.setItem('jarvis_last_activity', Date.now().toString());
  }

  isTokenExpired(): boolean {
    try {
      const metadata = localStorage.getItem(this.TOKEN_METADATA_KEY);
      if (!metadata) return true;
      
      const data: TokenData = JSON.parse(metadata);
      return Date.now() > data.expiresAt;
    } catch {
      return true;
    }
  }

  // Get token metadata
  private getTokenMetadata(): TokenData | null {
    try {
      const metadata = localStorage.getItem(this.TOKEN_METADATA_KEY);
      return metadata ? JSON.parse(metadata) : null;
    } catch {
      return null;
    }
  }

  // Get token from storage with fallback
  private getFromStorage(key: string): string | null {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  }

  // Check if tokens exist
  hasTokens(): boolean {
    return !!(this.getFromStorage(this.ACCESS_TOKEN_KEY) && this.getFromStorage(this.REFRESH_TOKEN_KEY));
  }

  // Get time until session expires
  getTimeUntilSessionExpiry(): number {
    const lastActivity = localStorage.getItem('jarvis_last_activity');
    if (!lastActivity) return 0;

    const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
    return Math.max(0, this.MAX_INACTIVE_TIME - timeSinceLastActivity);
  }
}

export const tokenStorage = new SecureTokenStorage();